// Base
import Image from 'next/image'

// Helpers
import { formatBlurImage, formatImage } from '@helpers/formatImage'
import { useMediaQuery } from '@helpers/useMediaQuery'

// Components
import Box from '@components/helpers/box/Box'
import Text from '@components/helpers/text/Text'
import Flex from '@components/helpers/flex/Flex'

// Styles
import styles from './DestinationCard.module.sass'

// Interfaces
import { DestinationData } from '@interfaces/destination'

// Icons
import { FiPlus } from 'react-icons/fi'
import { CheckmarkCircle } from 'icons/Icons'

interface DestinationCardProps {
  destination: DestinationData
  addDestination: Function
  removeDestination: Function
  disabled: boolean
  selected: boolean
}

const DestinationCard = ({
  destination,
  addDestination,
  removeDestination,
  disabled = false,
  selected
}: DestinationCardProps) => {
  const isDesktop = useMediaQuery(1168)

  const toggleDestination = (
    city: string,
    id: number,
    action: 'remove' | 'add'
  ) => {
    switch (action) {
      case 'add':
        !disabled && addDestination(city, id)
        break
      case 'remove':
        removeDestination(id)
        break
      default:
        break
    }
  }

  return (
    <Box
      borderRadius={{ sm: [8] }}
      overflow="hidden"
      className={`${styles.card} ${selected ? styles['added'] : ''}`}
      onClick={() => {
        toggleDestination(
          destination.name,
          destination.id,
          selected ? 'remove' : 'add'
        )
      }}
    >
      <Image
        src={formatImage(destination.image, 'medium')}
        placeholder="blur"
        blurDataURL={formatBlurImage(destination.image)}
        fill
        sizes="
          (min-width: 768px) 400px,
          136px
        "
        alt={destination.image.alternativeText || 'Destination Image'}
      />

      {selected && (
        <Flex
          flexDirection="column"
          center
          rowGap={{ sm: 4, lg: 8 }}
          className={styles.card__addedCheck}
        >
          <CheckmarkCircle size={isDesktop ? 32 : 24} />
        </Flex>
      )}

      {!selected && (
        <Box
          backgroundColor="white"
          borderRadius={{ sm: [0, 8, 0, 8] }}
          className={styles.card__add}
        >
          <Flex alignItems="center" justifyContent="center" fullHeight>
            <FiPlus size={23} color="#ED6F82" />
          </Flex>
        </Box>
      )}

      <Box padding={{ sm: selected ? [4] : [8] }} className={styles.card__name}>
        <Text
          tagName="p"
          variant={selected ? 'body-3' : 'body-1'}
          fontWeight={selected ? 500 : 700}
          color={selected ? 'grey500' : 'white'}
          align={selected ? 'center' : 'left'}
        >
          {selected ? 'Remove' : destination.name}
        </Text>
      </Box>
    </Box>
  )
}

export default DestinationCard
